import { useState, useCallback, useEffect, useRef } from "react";

interface UseImagePanOptions {
  zoom: number;
  open: boolean;
  fileId: string;
}

export function useImagePan({ zoom, open, fileId }: UseImagePanOptions) {
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setPan({ x: 0, y: 0 });
    }
  }, [fileId, open]);

  const resetPan = useCallback(() => {
    setPan({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - pan.x,
          y: e.clientY - pan.y,
        });
      }
    },
    [zoom, pan]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging && zoom > 1 && imageContainerRef.current) {
        const container = imageContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        const newPanX = e.clientX - dragStart.x;
        const newPanY = e.clientY - dragStart.y;

        const maxPan =
          Math.max(containerWidth, containerHeight) * (zoom - 1) * 0.5;
        const constrainedPanX = Math.max(-maxPan, Math.min(maxPan, newPanX));
        const constrainedPanY = Math.max(-maxPan, Math.min(maxPan, newPanY));

        setPan({
          x: constrainedPanX,
          y: constrainedPanY,
        });
      }
    },
    [isDragging, zoom, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return {
    pan,
    isDragging,
    imageContainerRef,
    handleMouseDown,
    resetPan,
  };
}

